import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, MapPin, Star } from "lucide-react";

const LINES = ["GLASS SO", "CLEAR, IT", "DISAPPEARS."];

const EASE = [0.16, 1, 0.3, 1];

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const ghostY = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      className="relative flex min-h-[100svh] flex-col justify-end overflow-hidden"
    >
      <div className="absolute inset-0" aria-hidden="true">
        <div className="absolute -top-40 right-[-12%] h-[38rem] w-[38rem] rounded-full bg-sky-200/50 blur-[130px]" />
        <div className="absolute bottom-[-25%] left-[-12%] h-[32rem] w-[32rem] rounded-full bg-sky-100/70 blur-[110px]" />
        <motion.span
          style={{ y: ghostY, WebkitTextStroke: "1px #E4E4E7" }}
          className="absolute left-1/2 top-[38%] -translate-x-1/2 -translate-y-1/2 select-none whitespace-nowrap font-display text-[24vw] font-black tracking-tighter text-transparent"
        >
          SKY-HIGH
        </motion.span>
      </div>

      <motion.div
        style={{ opacity: fade }}
        className="relative z-10 mx-auto w-full max-w-[1400px] px-6 pb-16 pt-40 md:px-12 md:pb-24"
      >
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.8, ease: EASE }}
          className="mb-8 flex items-center gap-3"
        >
          <span className="inline-flex items-center gap-1.5 rounded-full border border-zinc-950/15 bg-white/60 px-4 py-1.5 text-[11px] font-bold uppercase tracking-[0.25em] text-zinc-800 backdrop-blur-md">
            <MapPin className="h-3 w-3 text-sky-500" />
            Billings, Montana
          </span>
          <span className="hidden items-center gap-1.5 rounded-full border border-zinc-950/15 bg-white/60 px-4 py-1.5 text-[11px] font-bold uppercase tracking-[0.25em] text-zinc-800 backdrop-blur-md sm:inline-flex">
            <Star className="h-3 w-3 fill-sky-500 text-sky-500" />
            5-Star Rated
          </span>
        </motion.div>

        <h1
          data-testid="hero-heading"
          className="font-display text-[clamp(3.2rem,11vw,10.5rem)] font-black leading-[0.9] tracking-tighter text-zinc-950"
        >
          {LINES.map((line, i) => (
            <span key={line} className="block overflow-hidden pb-[0.08em]">
              <motion.span
                initial={{ y: "115%" }}
                animate={{ y: "0%" }}
                transition={{ delay: 0.45 + i * 0.16, duration: 1.15, ease: EASE }}
                className={`block ${i === 2 ? "text-sky-500" : ""}`}
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>

        <div className="mt-10 flex flex-col gap-8 md:flex-row md:items-end md:justify-between">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.1, duration: 0.9, ease: EASE }}
            className="max-w-md text-base leading-relaxed text-zinc-600 md:text-lg"
            data-testid="hero-subtext"
          >
            Sky-High Window Washing brings streak-free glass to homes
            and businesses across the Magic City — residential, commercial, and
            everything above the gutter line.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.25, duration: 0.9, ease: EASE }}
            className="flex items-center gap-4"
          >
            <a
              href="tel:4066963771"
              data-testid="hero-call-button"
              className="rounded-full bg-sky-500 px-8 py-4 text-sm font-bold uppercase tracking-[0.15em] text-white transition-all duration-300 hover:scale-105 hover:bg-zinc-950 active:scale-95"
            >
              Get a Free Quote
            </a>
            <a
              href="#services"
              data-testid="hero-explore-button"
              className="group flex h-12 w-12 items-center justify-center rounded-full border border-zinc-950/20 bg-white/60 backdrop-blur-md transition-transform duration-300 hover:scale-110"
              aria-label="Scroll to services"
            >
              <ArrowDown className="h-4 w-4 text-zinc-950 transition-transform duration-300 group-hover:translate-y-0.5" />
            </a>
          </motion.div>
        </div>
      </motion.div>
    </section>
  );
};
